import {
  useState,
  useMemo,
  Fragment,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  MultiSelect,
  Paper,
  TextInput,
  Textarea,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
  Divider,
  SimpleGrid,
  Tabs,
  Menu,
  Alert,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconRefresh,
  IconChevronDown,
  IconChevronRight,
  IconAlertTriangle,
  IconX,
  IconCheck,
  IconDotsVertical,
  IconInfoCircle,
  IconAlertCircle,
} from "@tabler/icons-react";
import {
  usePhysiciansAdmin,
  useCreatePhysician,
  useUpdatePhysician,
  useTogglePhysicianActive,
  usePhysicianLinkedPractices,
  usePhysicianLinkedSurgeryCenters,
} from "../../../hooks/admin/usePhysicians";
import {
  usePhysicianReportDeliveryByPhysician,
  useCreatePhysicianReportDelivery,
  useUpdatePhysicianReportDelivery,
  useTogglePhysicianReportDeliveryActive,
  useDeletePhysicianReportDelivery,
} from "../../../hooks/admin/usePhysicianReportDelivery";
import { useReportDeliveryMethodsAdmin } from "../../../hooks/admin/useReportDeliveryMethods";
import { useActiveReportTemplates } from "../../../hooks/admin/useReportTemplates";
import { useUsersAdmin } from "../../../hooks/admin/useUsers";
import PhysicianForm, {
  PhysicianFormValues,
} from "../../../components/admin/physicians/PhysicianForm";
import { PortalAccessSection } from "../../../components/admin/portal-access/PortalAccessSection";
import { PortalAccessIndicator } from "../../../components/admin/portal-access/PortalAccessIndicator";
import { AddPortalGrantModal } from "../../../components/admin/portal-access/AddPortalGrantModal";
import {
  usePortalAccessSubjectIdSet,
  usePortalAccessGrantsForSubject,
} from "../../../hooks/admin/usePortalAccess";
import {
  Physician,
  physiciansService,
} from "../../../services/admin/physicians.service";
import {
  PhysicianReportDelivery,
  CreatePhysicianReportDeliveryInput,
} from "../../../services/admin/physicianreportdelivery.service";

interface PhysicianFilters {
  status?: "all" | "active" | "inactive";
  preferredPathologistIds?: string[];
  reportTemplateIds?: string[];
}

const ITEMS_PER_PAGE = 20;

export default function PhysiciansAdminPage() {
  const queryClient = useQueryClient();
  // Modal states
  const [
    physicianModalOpened,
    { open: openPhysicianModal, close: closePhysicianModal },
  ] = useDisclosure(false);
  const [
    deliveryModalOpened,
    { open: openDeliveryModal, close: closeDeliveryModal },
  ] = useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);
  const [
    portalGrantModalOpened,
    { open: openPortalGrantModal, close: closePortalGrantModal },
  ] = useDisclosure(false);

  // URL state
  const [searchParams, setSearchParams] = useSearchParams();

  const VALID_STATUSES = ["active", "inactive"] as const;
  const VALID_TABS = [
    "portal-access",
    "practices",
    "surgery-centers",
    "delivery",
  ] as const;

  const urlSearch = searchParams.get("search") ?? "";
  const urlStatus = VALID_STATUSES.includes(
    searchParams.get("status") as (typeof VALID_STATUSES)[number]
  )
    ? (searchParams.get("status") as "active" | "inactive")
    : "all";
  const urlPathologists = searchParams.get("pathologists")
    ? searchParams.get("pathologists")!.split(",").filter(Boolean)
    : [];
  const urlTemplates = searchParams.get("templates")
    ? searchParams.get("templates")!.split(",").filter(Boolean)
    : [];
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const expandedRow = searchParams.get("physician") ?? null;
  const activeTab = VALID_TABS.includes(
    searchParams.get("tab") as (typeof VALID_TABS)[number]
  )
    ? (searchParams.get("tab") as string)
    : "portal-access";

  const filters = useMemo<PhysicianFilters>(
    () => ({
      status: urlStatus,
      preferredPathologistIds: urlPathologists.length
        ? urlPathologists
        : undefined,
      reportTemplateIds: urlTemplates.length ? urlTemplates : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [urlStatus, searchParams.get("pathologists"), searchParams.get("templates")]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Page state
  const [editingPhysician, setEditingPhysician] = useState<Physician | null>(
    null
  );
  const [editingDelivery, setEditingDelivery] =
    useState<PhysicianReportDelivery | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{
    type: "physician" | "delivery";
    item: Physician | PhysicianReportDelivery;
  } | null>(null);
  const [pendingFilters, setPendingFilters] = useState<PhysicianFilters>({
    status: "all",
  });

  // Local input state for search (responsive typing; URL updated on debounce)
  const [inputSearch, setInputSearch] = useState(urlSearch);
  const [debouncedSearch] = useDebouncedValue(inputSearch, 300);

  // Sync input when URL changes externally (back/forward navigation)
  const prevUrlSearch = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch !== prevUrlSearch.current) {
      prevUrlSearch.current = urlSearch;
      setInputSearch(urlSearch);
    }
  }, [urlSearch]);

  // Write debounced search to URL and reset page
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateParams({ search: debouncedSearch || null, page: null });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Delivery method form state
  const [deliveryPhysicianId, setDeliveryPhysicianId] = useState<string>("");
  const [deliveryMethodId, setDeliveryMethodId] = useState<string>("");
  const [deliveryEmail, setDeliveryEmail] = useState<string>("");
  const [deliveryFax, setDeliveryFax] = useState<string>("");
  const [deliveryPhone, setDeliveryPhone] = useState<string>("");
  const [deliveryAddress, setDeliveryAddress] = useState<string>("");
  const [deliveryHl7Host, setDeliveryHl7Host] = useState<string>("");
  const [deliveryHl7Port, setDeliveryHl7Port] = useState<string>("");
  const [deliveryHl7Facility, setDeliveryHl7Facility] = useState<string>("");
  const [deliveryHl7ApplicationId, setDeliveryHl7ApplicationId] =
    useState<string>("");
  const [
    deliveryRemoteConnectionIdentifier,
    setDeliveryRemoteConnectionIdentifier,
  ] = useState<string>("");
  // Physician hooks
  const { data: physicians = [], isLoading, refetch } = usePhysiciansAdmin();
  const { subjectIdSet: physiciansWithPortal } =
    usePortalAccessSubjectIdSet("physician");
  const createPhysician = useCreatePhysician();
  const updatePhysician = useUpdatePhysician();
  const togglePhysicianActive = useTogglePhysicianActive();

  // Reference data hooks
  const { data: users = [] } = useUsersAdmin();
  const { data: reportTemplates = [] } = useActiveReportTemplates();
  const { data: deliveryMethods = [] } = useReportDeliveryMethodsAdmin();

  // Report delivery method hooks
  const {
    data: currentDeliveryMethods = [],
    isLoading: isLoadingDeliveryMethods,
  } = usePhysicianReportDeliveryByPhysician(expandedRow);
  const createDelivery = useCreatePhysicianReportDelivery();
  const updateDelivery = useUpdatePhysicianReportDelivery();
  const toggleDeliveryActive = useTogglePhysicianReportDeliveryActive();
  const deleteDelivery = useDeletePhysicianReportDelivery();

  // Linked practices hook for the expanded row
  const { data: expandedLinkedPractices = [] } =
    usePhysicianLinkedPractices(expandedRow);
  const { data: expandedLinkedSurgeryCenters = [] } =
    usePhysicianLinkedSurgeryCenters(expandedRow);

  // Get pathologists for filter dropdown
  const pathologists = users.filter((u) => u.is_active);

  // Filter physicians
  const filteredPhysicians = useMemo(() => {
    let result = physicians;

    // Apply search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.firstName?.toLowerCase().includes(query) ||
          p.lastName?.toLowerCase().includes(query) ||
          p.npi?.toLowerCase().includes(query) ||
          p.phone?.toLowerCase().includes(query) ||
          p.fax?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (filters.status === "active") {
      result = result.filter((p) => p.isActive);
    } else if (filters.status === "inactive") {
      result = result.filter((p) => !p.isActive);
    }

    // Apply preferred pathologist filter
    if (filters.preferredPathologistIds?.length) {
      result = result.filter(
        (p) =>
          p.preferredPathologistId &&
          filters.preferredPathologistIds!.includes(p.preferredPathologistId)
      );
    }

    // Apply report template filter
    if (filters.reportTemplateIds?.length) {
      result = result.filter(
        (p) =>
          p.reportTemplateId &&
          filters.reportTemplateIds!.includes(p.reportTemplateId)
      );
    }

    return result;
  }, [physicians, debouncedSearch, filters]);

  // Pagination
  const totalPages = Math.ceil(filteredPhysicians.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredPhysicians.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPhysicians, page]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.status !== "all" ||
    (filters.preferredPathologistIds &&
      filters.preferredPathologistIds.length > 0) ||
    (filters.reportTemplateIds && filters.reportTemplateIds.length > 0);

  // Handlers for Physicians
  const handleAddPhysician = () => {
    setEditingPhysician(null);
    openPhysicianModal();
  };

  const handleEditPhysician = (physician: Physician) => {
    setEditingPhysician(physician);
    openPhysicianModal();
  };

  const handlePhysicianSubmit = async (values: PhysicianFormValues) => {
    const {
      isRemote,
      remoteShippingStreet,
      remoteShippingCity,
      remoteShippingState,
      remoteShippingZip,
      ...baseValues
    } = values;

    try {
      if (editingPhysician) {
        await updatePhysician.mutateAsync({
          id: editingPhysician.id,
          ...baseValues,
        });

        try {
          await physiciansService.updateRemoteShippingAddress(
            editingPhysician.id,
            {
              isRemote,
              address: {
                street: remoteShippingStreet || null,
                city: remoteShippingCity || null,
                state: remoteShippingState || null,
                zip: remoteShippingZip || null,
              },
            }
          );
          await queryClient.invalidateQueries({
            queryKey: ["admin", "physicians"],
          });
        } catch {
          notifications.show({
            title: "Remote Address Save Failed",
            message:
              "Physician was updated but remote shipping address could not be saved.",
            color: "red",
          });
        }
      } else {
        const result = await createPhysician.mutateAsync(baseValues);
        if (result && result.id) {
          try {
            await physiciansService.updateRemoteShippingAddress(result.id, {
              isRemote,
              address: {
                street: remoteShippingStreet || null,
                city: remoteShippingCity || null,
                state: remoteShippingState || null,
                zip: remoteShippingZip || null,
              },
            });
            await queryClient.invalidateQueries({
              queryKey: ["admin", "physicians"],
            });
          } catch {
            notifications.show({
              title: "Remote Address Save Failed",
              message:
                "Physician was created but remote shipping address could not be saved.",
              color: "red",
            });
          }
        }
      }
      closePhysicianModal();
    } catch {
      // Handled by query/mutations
    }
  };

  const handleTogglePhysicianActive = async (physician: Physician) => {
    await togglePhysicianActive.mutateAsync({
      id: physician.id,
      isActive: !physician.isActive,
    });
  };

  // Handlers for Delivery Methods
  const handleAddDeliveryMethod = (
    physicianId: string,
    physician?: Physician
  ) => {
    setEditingDelivery(null);
    setDeliveryPhysicianId(physicianId);
    setDeliveryMethodId("");
    setDeliveryEmail(physician?.email || "");
    setDeliveryFax(physician?.fax || "");
    setDeliveryPhone(physician?.phone || "");
    setDeliveryAddress("");
    setDeliveryHl7Host("");
    setDeliveryHl7Port("");
    setDeliveryHl7Facility("");
    setDeliveryHl7ApplicationId("");
    setDeliveryRemoteConnectionIdentifier("");
    openDeliveryModal();
  };

  const handleEditDeliveryMethod = (delivery: PhysicianReportDelivery) => {
    setEditingDelivery(delivery);
    setDeliveryPhysicianId(delivery.physicianId);
    setDeliveryMethodId(delivery.reportDeliveryMethodId);
    setDeliveryEmail(delivery.email || "");
    setDeliveryFax(delivery.fax || "");
    setDeliveryPhone(delivery.phone || "");
    setDeliveryAddress(delivery.address || "");
    setDeliveryHl7Host(delivery.hl7Host || "");
    setDeliveryHl7Port(delivery.hl7Port?.toString() || "");
    setDeliveryHl7Facility(delivery.hl7Facility || "");
    setDeliveryHl7ApplicationId(delivery.hl7ApplicationId || "");
    setDeliveryRemoteConnectionIdentifier(
      delivery.remoteConnectionIdentifier || ""
    );
    openDeliveryModal();
  };

  const handleDeliverySubmit = async () => {
    if (!deliveryMethodId) return;

    const data: CreatePhysicianReportDeliveryInput = {
      physicianId: deliveryPhysicianId,
      reportDeliveryMethodId: deliveryMethodId,
      email: deliveryEmail || null,
      fax: deliveryFax || null,
      phone: deliveryPhone || null,
      address: deliveryAddress || null,
      practiceId: null,
      hl7Host: deliveryHl7Host || null,
      hl7Port:
        deliveryHl7Port !== "" && deliveryHl7Port !== null
          ? parseInt(deliveryHl7Port, 10)
          : null,
      hl7Facility: deliveryHl7Facility || null,
      hl7ApplicationId: deliveryHl7ApplicationId || null,
      remoteConnectionIdentifier: deliveryRemoteConnectionIdentifier || null,
    };

    if (editingDelivery) {
      await updateDelivery.mutateAsync({
        id: editingDelivery.id,
        ...data,
      });
    } else {
      await createDelivery.mutateAsync(data);
    }
    closeDeliveryModal();
  };

  const handleToggleDeliveryActive = async (
    delivery: PhysicianReportDelivery
  ) => {
    await toggleDeliveryActive.mutateAsync(delivery.id);
  };

  const handleDeleteDelivery = (delivery: PhysicianReportDelivery) => {
    setItemToDelete({ type: "delivery", item: delivery });
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (itemToDelete?.type === "delivery") {
      await deleteDelivery.mutateAsync(
        (itemToDelete.item as PhysicianReportDelivery).id
      );
    }
    closeDeleteModal();
    setItemToDelete(null);
  };

  const toggleRow = (physicianId: string) => {
    if (expandedRow === physicianId) {
      updateParams({ physician: null, tab: null });
    } else {
      updateParams({ physician: physicianId, tab: null });
    }
  };

  // Filter handlers
  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    updateParams({
      status:
        pendingFilters.status !== "all" ? pendingFilters.status ?? null : null,
      pathologists: pendingFilters.preferredPathologistIds?.length
        ? pendingFilters.preferredPathologistIds.join(",")
        : null,
      templates: pendingFilters.reportTemplateIds?.length
        ? pendingFilters.reportTemplateIds.join(",")
        : null,
      page: null,
    });
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setPendingFilters({ status: "all" });
    updateParams({
      status: null,
      pathologists: null,
      templates: null,
      page: null,
    });
  };

  const handleClearPendingFilters = () => {
    setPendingFilters({ status: "all" });
  };

  // Get the current selected delivery method for conditional fields
  const selectedMethod = deliveryMethods.find((m) => m.id === deliveryMethodId);
  const methodName = selectedMethod?.name?.toLowerCase() || "";

  // Portal delivery needs at least one eligible portal grant on the physician.
  // Fetch only while the delivery modal is open AND a portal method is selected.
  const selectedMethodIsPortal = methodName.startsWith("portal");
  const {
    data: deliveryPhysicianGrants = [],
    isLoading: deliveryGrantsLoading,
  } = usePortalAccessGrantsForSubject(
    "physician",
    deliveryModalOpened && selectedMethodIsPortal ? deliveryPhysicianId : null
  );

  const portalNeedsSetup =
    selectedMethodIsPortal &&
    !!deliveryPhysicianId &&
    !deliveryGrantsLoading &&
    !deliveryPhysicianGrants.some(
      (g) => g.state === "active" || g.state === "invited"
    );

  const portalIsPending =
    selectedMethodIsPortal &&
    !!deliveryPhysicianId &&
    !deliveryGrantsLoading &&
    deliveryPhysicianGrants.some((g) => g.state === "invited") &&
    !deliveryPhysicianGrants.some((g) => g.state === "active");

  const deliveryPhysician = useMemo(
    () => physicians.find((p) => p.id === deliveryPhysicianId) ?? null,
    [physicians, deliveryPhysicianId]
  );
  const deliveryPhysicianLabel = deliveryPhysician
    ? `${deliveryPhysician.firstName} ${deliveryPhysician.lastName}`
    : "This physician";
  const deliveryPortalPrimaryDefaults = useMemo(
    () => ({
      email: deliveryPhysician?.email ?? "",
      firstName: deliveryPhysician?.firstName ?? "",
      lastName: deliveryPhysician?.lastName ?? "",
      role: "Self",
    }),
    [deliveryPhysician]
  );

  const handleOpenPortalGrantModal = useCallback(() => {
    openPortalGrantModal();
  }, [openPortalGrantModal]);

  const handleClosePortalGrantModal = useCallback(() => {
    closePortalGrantModal();
  }, [closePortalGrantModal]);

  // Options for dropdowns
  const pathologistOptions = pathologists.map((p) => ({
    value: p.id,
    label: p.full_name,
  }));

  const templateOptions = reportTemplates.map((t) => ({
    value: t.id,
    label: t.isDefault ? `${t.name} (Default)` : t.name,
  }));

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Physicians</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage referring physicians and their report delivery methods
              </Text>
            </div>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleAddPhysician}
            >
              Add Physician
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            {/* Search Section */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name, NPI, phone, or fax..."
                  value={inputSearch}
                  onChange={(e) => setInputSearch(e.currentTarget.value)}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            {/* Filters Section */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {filters.status !== "all" && (
                      <Badge variant="light" color="blue">
                        {filters.status === "active" ? "Active" : "Inactive"}
                      </Badge>
                    )}
                    {filters.preferredPathologistIds &&
                      filters.preferredPathologistIds.length > 0 && (
                        <Badge variant="light" color="violet">
                          {filters.preferredPathologistIds.length} Pathologist
                          {filters.preferredPathologistIds.length > 1
                            ? "s"
                            : ""}
                        </Badge>
                      )}
                    {filters.reportTemplateIds &&
                      filters.reportTemplateIds.length > 0 && (
                        <Badge variant="light" color="cyan">
                          {filters.reportTemplateIds.length} Template
                          {filters.reportTemplateIds.length > 1 ? "s" : ""}
                        </Badge>
                      )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredPhysicians.length} physician
                  {filteredPhysicians.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Table Header with Pagination */}
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Physicians ({filteredPhysicians.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredPhysicians.length
                      )}{" "}
                      of {filteredPhysicians.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={(n) =>
                        updateParams({ page: n > 1 ? String(n) : null })
                      }
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {/* Table */}
              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredPhysicians.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || inputSearch
                    ? "No physicians match the current filters"
                    : "No physicians found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }} />
                      <Table.Th>Name</Table.Th>
                      <Table.Th>NPI</Table.Th>
                      <Table.Th>Phone</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th style={{ width: 100 }}>Status</Table.Th>
                      <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((physician) => (
                      <Fragment key={physician.id}>
                        <Table.Tr>
                          {/* Expand Toggle */}
                          <Table.Td>
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              onClick={() => toggleRow(physician.id)}
                            >
                              {expandedRow === physician.id ? (
                                <IconChevronDown size={16} />
                              ) : (
                                <IconChevronRight size={16} />
                              )}
                            </ActionIcon>
                          </Table.Td>

                          {/* Name */}
                          <Table.Td>
                            <Group gap="xs" wrap="nowrap" align="center">
                              <PortalAccessIndicator
                                hasAccess={physiciansWithPortal.has(
                                  physician.id
                                )}
                                subjectLabel={`Dr. ${physician.lastName}`}
                              />
                              <Stack gap={2}>
                                <Text size="sm" fw={500}>
                                  {physician.firstName} {physician.lastName}
                                </Text>
                                {physician.isRemote && (
                                  <Group gap={4}>
                                    {(() => {
                                      const isComplete =
                                        !!physician.remoteShippingStreet &&
                                        !!physician.remoteShippingCity &&
                                        !!physician.remoteShippingState &&
                                        !!physician.remoteShippingZip;
                                      return (
                                        <Badge
                                          color={isComplete ? "blue" : "orange"}
                                          variant="light"
                                          size="xs"
                                        >
                                          {isComplete
                                            ? "Remote: Address set"
                                            : "Remote: Address required"}
                                        </Badge>
                                      );
                                    })()}
                                  </Group>
                                )}
                              </Stack>
                            </Group>
                          </Table.Td>

                          {/* NPI */}
                          <Table.Td>
                            <Text size="sm">{physician.npi || "—"}</Text>
                          </Table.Td>

                          {/* Phone */}
                          <Table.Td>
                            <Text size="sm">{physician.phone || "—"}</Text>
                          </Table.Td>

                          {/* Email */}
                          <Table.Td>
                            <Text
                              size="sm"
                              style={{ maxWidth: 180 }}
                              truncate="end"
                            >
                              {physician.email || "—"}
                            </Text>
                          </Table.Td>

                          {/* Status */}
                          <Table.Td>
                            <Badge
                              color={physician.isActive ? "green" : "red"}
                              variant="light"
                            >
                              {physician.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </Table.Td>

                          {/* Actions */}
                          <Table.Td>
                            <Menu
                              position="bottom-end"
                              offset={4}
                              withinPortal
                              shadow="md"
                            >
                              <Menu.Target>
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label="Row actions"
                                >
                                  <IconDotsVertical size={18} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={<IconEdit size={16} />}
                                  onClick={() => handleEditPhysician(physician)}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={
                                    physician.isActive ? (
                                      <IconX size={16} />
                                    ) : (
                                      <IconCheck size={16} />
                                    )
                                  }
                                  color={physician.isActive ? "red" : "green"}
                                  onClick={() =>
                                    void handleTogglePhysicianActive(physician)
                                  }
                                >
                                  {physician.isActive
                                    ? "Deactivate"
                                    : "Activate"}
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Table.Td>
                        </Table.Tr>

                        {/* Expanded Row - Delivery Methods */}
                        {expandedRow === physician.id && (
                          <Table.Tr>
                            <Table.Td colSpan={7} p={0}>
                              <Box pl="xl" pr="md" pb="md" pt="sm" bg="gray.0">
                                {/* Physician detail strip */}
                                <SimpleGrid
                                  cols={{ base: 2, sm: 3, md: 6 }}
                                  spacing="xs"
                                  mb="md"
                                  pb="sm"
                                  style={{
                                    borderBottom:
                                      "1px solid var(--mantine-color-gray-3)",
                                  }}
                                >
                                  <Box>
                                    <Text
                                      size="xs"
                                      fw={600}
                                      c="dimmed"
                                      tt="uppercase"
                                      style={{ letterSpacing: "0.05em" }}
                                    >
                                      Email
                                    </Text>
                                    <Text size="sm">
                                      {physician.email || "—"}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      size="xs"
                                      fw={600}
                                      c="dimmed"
                                      tt="uppercase"
                                      style={{ letterSpacing: "0.05em" }}
                                    >
                                      Phone
                                    </Text>
                                    <Text size="sm">
                                      {physician.phone || "—"}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      size="xs"
                                      fw={600}
                                      c="dimmed"
                                      tt="uppercase"
                                      style={{ letterSpacing: "0.05em" }}
                                    >
                                      Fax
                                    </Text>
                                    <Text size="sm">
                                      {physician.fax || "—"}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      size="xs"
                                      fw={600}
                                      c="dimmed"
                                      tt="uppercase"
                                      style={{ letterSpacing: "0.05em" }}
                                    >
                                      Pathologist
                                    </Text>
                                    <Text size="sm">
                                      {physician.preferredPathologist
                                        ?.fullName || "—"}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      size="xs"
                                      fw={600}
                                      c="dimmed"
                                      tt="uppercase"
                                      style={{ letterSpacing: "0.05em" }}
                                    >
                                      Report Template
                                    </Text>
                                    <Text size="sm">
                                      {physician.reportTemplate?.name || "—"}
                                    </Text>
                                  </Box>
                                  <Box>
                                    <Text
                                      size="xs"
                                      fw={600}
                                      c="dimmed"
                                      tt="uppercase"
                                      style={{ letterSpacing: "0.05em" }}
                                    >
                                      Remote Shipping
                                    </Text>
                                    <Text size="sm">
                                      {!physician.isRemote
                                        ? "No"
                                        : (() => {
                                            const isComplete =
                                              !!physician.remoteShippingStreet &&
                                              !!physician.remoteShippingCity &&
                                              !!physician.remoteShippingState &&
                                              !!physician.remoteShippingZip;
                                            return isComplete
                                              ? "Address set"
                                              : "Address required";
                                          })()}
                                    </Text>
                                  </Box>
                                </SimpleGrid>

                                {/* Tabs: Portal Access | Practices | Surgery Centers | Delivery Methods */}
                                <Tabs
                                  value={activeTab}
                                  onChange={(tab) =>
                                    updateParams({
                                      tab:
                                        tab && tab !== "portal-access"
                                          ? tab
                                          : null,
                                    })
                                  }
                                >
                                  <Tabs.List mb="sm">
                                    <Tabs.Tab value="portal-access">
                                      Portal Access
                                    </Tabs.Tab>
                                    <Tabs.Tab value="practices">
                                      Practices
                                    </Tabs.Tab>
                                    <Tabs.Tab value="surgery-centers">
                                      Surgery Centers
                                    </Tabs.Tab>
                                    <Tabs.Tab value="delivery">
                                      Delivery Methods
                                    </Tabs.Tab>
                                  </Tabs.List>

                                  {/* Portal Access tab */}
                                  <Tabs.Panel value="portal-access">
                                    <PortalAccessSection
                                      subjectType="physician"
                                      subjectId={physician.id}
                                      subjectName={`${physician.firstName} ${physician.lastName}`}
                                      subjectEmail={physician.email ?? null}
                                      subjectFirstName={physician.firstName}
                                      subjectLastName={physician.lastName}
                                    />
                                  </Tabs.Panel>

                                  {/* Practices tab */}
                                  <Tabs.Panel value="practices">
                                    {expandedLinkedPractices.length === 0 ? (
                                      <Text size="sm" c="dimmed" py="xs">
                                        No linked practices.
                                      </Text>
                                    ) : (
                                      <Table>
                                        <Table.Thead>
                                          <Table.Tr>
                                            <Table.Th>Practice</Table.Th>
                                          </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                          {expandedLinkedPractices.map((lp) => (
                                            <Table.Tr key={lp.practice.id}>
                                              <Table.Td>
                                                <Group gap="xs">
                                                  <Text size="sm" fw={500}>
                                                    {lp.practice.name}
                                                  </Text>
                                                  {lp.isPrimary && (
                                                    <Badge
                                                      size="xs"
                                                      variant="light"
                                                      color="blue"
                                                    >
                                                      Primary
                                                    </Badge>
                                                  )}
                                                </Group>
                                              </Table.Td>
                                            </Table.Tr>
                                          ))}
                                        </Table.Tbody>
                                      </Table>
                                    )}
                                  </Tabs.Panel>

                                  {/* Surgery Centers tab (read-only — managed from Surgery Centers admin page) */}
                                  <Tabs.Panel value="surgery-centers">
                                    {expandedLinkedSurgeryCenters.length ===
                                    0 ? (
                                      <Text size="sm" c="dimmed" py="xs">
                                        No linked surgery centers.
                                      </Text>
                                    ) : (
                                      <Table>
                                        <Table.Thead>
                                          <Table.Tr>
                                            <Table.Th>
                                              Surgery Center / Lab
                                            </Table.Th>
                                            <Table.Th style={{ width: 100 }}>
                                              Type
                                            </Table.Th>
                                          </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                          {expandedLinkedSurgeryCenters.map(
                                            (lsc) => (
                                              <Table.Tr
                                                key={lsc.surgeryCenterLab.id}
                                              >
                                                <Table.Td>
                                                  <Group gap="xs">
                                                    <Text size="sm" fw={500}>
                                                      {
                                                        lsc.surgeryCenterLab
                                                          .name
                                                      }
                                                    </Text>
                                                    {lsc.isPrimary && (
                                                      <Badge
                                                        size="xs"
                                                        variant="light"
                                                        color="blue"
                                                      >
                                                        Primary
                                                      </Badge>
                                                    )}
                                                  </Group>
                                                </Table.Td>
                                                <Table.Td>
                                                  <Badge
                                                    size="xs"
                                                    variant="light"
                                                    color={
                                                      lsc.surgeryCenterLab
                                                        .type ===
                                                      "surgery_center"
                                                        ? "blue"
                                                        : "grape"
                                                    }
                                                  >
                                                    {lsc.surgeryCenterLab
                                                      .type === "surgery_center"
                                                      ? "Surgery Center"
                                                      : "Lab"}
                                                  </Badge>
                                                </Table.Td>
                                              </Table.Tr>
                                            )
                                          )}
                                        </Table.Tbody>
                                      </Table>
                                    )}
                                    <Text size="xs" c="dimmed" mt="xs">
                                      Links are managed from the Surgery Centers
                                      admin page.
                                    </Text>
                                  </Tabs.Panel>

                                  {/* Delivery Methods tab */}
                                  <Tabs.Panel value="delivery">
                                    <Group justify="flex-end" mb="sm">
                                      <Button
                                        size="xs"
                                        variant="light"
                                        leftSection={<IconPlus size={14} />}
                                        onClick={() =>
                                          handleAddDeliveryMethod(
                                            physician.id,
                                            physician
                                          )
                                        }
                                      >
                                        Add Delivery Method
                                      </Button>
                                    </Group>
                                    {isLoadingDeliveryMethods ? (
                                      <Center py="md">
                                        <Loader size="sm" />
                                      </Center>
                                    ) : currentDeliveryMethods.length === 0 ? (
                                      <Group gap="xs" py="sm">
                                        <IconAlertTriangle
                                          size={16}
                                          color="orange"
                                        />
                                        <Text size="sm" c="orange">
                                          No delivery methods configured. Add at
                                          least one delivery method.
                                        </Text>
                                      </Group>
                                    ) : (
                                      <Stack gap="xs">
                                        {currentDeliveryMethods.map(
                                          (delivery, index) => {
                                            const isLast =
                                              index ===
                                              currentDeliveryMethods.length - 1;
                                            const deliveryMethodName =
                                              delivery.reportDeliveryMethod
                                                ?.name || "Unknown";
                                            const dmn =
                                              deliveryMethodName.toLowerCase();
                                            const isPortal =
                                              dmn.startsWith("portal");
                                            const isHl7 = dmn.startsWith("hl7");
                                            const isEmail =
                                              dmn.startsWith("email");
                                            const isFax = dmn.startsWith("fax");
                                            const isShip =
                                              dmn.startsWith("ship") ||
                                              dmn === "mail";
                                            const isRemote =
                                              dmn.includes("remote print");
                                            const destination =
                                              isPortal || isEmail
                                                ? delivery.email || ""
                                                : isHl7
                                                ? [
                                                    delivery.hl7Host &&
                                                    delivery.hl7Port
                                                      ? `${delivery.hl7Host}:${delivery.hl7Port}`
                                                      : delivery.hl7Host || "",
                                                    delivery.hl7Facility
                                                      ? `facility: ${delivery.hl7Facility}`
                                                      : "",
                                                    delivery.hl7ApplicationId
                                                      ? `app: ${delivery.hl7ApplicationId}`
                                                      : "",
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ")
                                                : isFax
                                                ? delivery.fax || ""
                                                : isRemote
                                                ? delivery.remoteConnectionIdentifier ||
                                                  ""
                                                : isShip
                                                ? [
                                                    delivery.address || "",
                                                    delivery.phone || "",
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" · ")
                                                : delivery.fax ||
                                                  delivery.email ||
                                                  delivery.address ||
                                                  "";

                                            return (
                                              <Group
                                                key={delivery.id}
                                                justify="space-between"
                                                wrap="nowrap"
                                              >
                                                <Group
                                                  gap="xs"
                                                  wrap="nowrap"
                                                  style={{
                                                    minWidth: 0,
                                                    flex: 1,
                                                  }}
                                                >
                                                  <Text
                                                    size="sm"
                                                    c="dimmed"
                                                    ff="monospace"
                                                    style={{ flexShrink: 0 }}
                                                  >
                                                    {isLast ? "└─" : "├─"}
                                                  </Text>
                                                  <Text
                                                    size="sm"
                                                    fw={500}
                                                    style={{ flexShrink: 0 }}
                                                  >
                                                    {deliveryMethodName}
                                                    {destination ? ":" : ""}
                                                  </Text>
                                                  {destination && (
                                                    <Text
                                                      size="sm"
                                                      c="dimmed"
                                                      truncate="end"
                                                      style={{
                                                        minWidth: 0,
                                                        overflow: "hidden",
                                                      }}
                                                    >
                                                      {destination}
                                                    </Text>
                                                  )}
                                                </Group>
                                                <Group gap="xs" wrap="nowrap">
                                                  <Badge
                                                    size="sm"
                                                    color={
                                                      delivery.isActive
                                                        ? "green"
                                                        : "red"
                                                    }
                                                    variant="light"
                                                  >
                                                    {delivery.isActive
                                                      ? "Active"
                                                      : "Inactive"}
                                                  </Badge>
                                                  <Tooltip label="Edit">
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      onClick={() =>
                                                        handleEditDeliveryMethod(
                                                          delivery
                                                        )
                                                      }
                                                    >
                                                      <IconEdit size={14} />
                                                    </ActionIcon>
                                                  </Tooltip>
                                                  <Tooltip
                                                    label={
                                                      delivery.isActive
                                                        ? "Deactivate"
                                                        : "Activate"
                                                    }
                                                  >
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      color={
                                                        delivery.isActive
                                                          ? "red"
                                                          : "green"
                                                      }
                                                      onClick={() =>
                                                        handleToggleDeliveryActive(
                                                          delivery
                                                        )
                                                      }
                                                    >
                                                      {delivery.isActive ? (
                                                        <IconX size={14} />
                                                      ) : (
                                                        <IconCheck size={14} />
                                                      )}
                                                    </ActionIcon>
                                                  </Tooltip>
                                                  <Tooltip label="Remove">
                                                    <ActionIcon
                                                      size="sm"
                                                      variant="subtle"
                                                      color="red"
                                                      onClick={() =>
                                                        handleDeleteDelivery(
                                                          delivery
                                                        )
                                                      }
                                                    >
                                                      <IconTrash size={14} />
                                                    </ActionIcon>
                                                  </Tooltip>
                                                </Group>
                                              </Group>
                                            );
                                          }
                                        )}
                                      </Stack>
                                    )}
                                  </Tabs.Panel>
                                </Tabs>
                              </Box>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Fragment>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit Physician Modal */}
        <Modal.Root
          opened={physicianModalOpened}
          onClose={closePhysicianModal}
          size="lg"
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingPhysician ? "Edit Physician" : "Add New Physician"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <PhysicianForm
                key={editingPhysician?.id || "new"}
                initialValues={editingPhysician || undefined}
                onSubmit={handlePhysicianSubmit}
              />
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closePhysicianModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="physician-form"
                loading={createPhysician.isPending || updatePhysician.isPending}
              >
                {editingPhysician ? "Update" : "Create"}
              </Button>
            </Group>
          </Modal.Content>
        </Modal.Root>

        {/* Add/Edit Delivery Method Modal */}
        <Modal.Root
          opened={deliveryModalOpened}
          onClose={closeDeliveryModal}
          size="md"
          closeOnClickOutside={false}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            },
          }}
        >
          <Modal.Overlay />
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>
                {editingDelivery
                  ? "Edit Delivery Method"
                  : "Add Delivery Method"}
              </Modal.Title>
              <Modal.CloseButton />
            </Modal.Header>

            <Modal.Body style={{ flex: 1, overflow: "auto" }}>
              <Stack gap="md">
                <Select
                  label="Delivery Method"
                  placeholder="Select delivery method..."
                  data={deliveryMethods
                    .filter(
                      (m) =>
                        m.isActive && !m.name?.toLowerCase().startsWith("hl7")
                    )
                    .map((m) => ({
                      value: m.id,
                      label: m.name,
                    }))}
                  value={deliveryMethodId}
                  onChange={(value) => setDeliveryMethodId(value || "")}
                  required
                />

                {/* Conditional fields based on method type */}
                {methodName === "email" && (
                  <TextInput
                    label="Email Address"
                    placeholder="Enter email address"
                    value={deliveryEmail}
                    onChange={(e) => setDeliveryEmail(e.currentTarget.value)}
                  />
                )}

                {methodName === "fax" && (
                  <TextInput
                    label="Fax Number"
                    placeholder="Enter fax number"
                    value={deliveryFax}
                    onChange={(e) => setDeliveryFax(e.currentTarget.value)}
                  />
                )}

                {(methodName.startsWith("ship") || methodName === "mail") && (
                  <Textarea
                    label="Mailing Address / Shipping Address"
                    placeholder="Enter full address"
                    autosize
                    minRows={2}
                    maxRows={4}
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.currentTarget.value)}
                  />
                )}

                {/* Portal delivery — routing is implicit by recipient. */}
                {methodName.startsWith("portal") && (
                  <>
                    {portalNeedsSetup ? (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="yellow"
                        variant="light"
                        title="Set up portal access first"
                        role="alert"
                      >
                        <Stack gap="sm">
                          <Text size="sm">
                            {deliveryPhysicianLabel} has no active or invited
                            portal access grants yet. Add at least one portal
                            grant so the Portal delivery method has a recipient
                            to route to.
                          </Text>
                          <Group>
                            <Button
                              size="xs"
                              variant="filled"
                              color="yellow"
                              onClick={handleOpenPortalGrantModal}
                            >
                              Set up portal access
                            </Button>
                          </Group>
                        </Stack>
                      </Alert>
                    ) : (
                      <Stack gap="xs">
                        {portalIsPending && (
                          <Alert
                            icon={<IconInfoCircle size={16} />}
                            color="blue"
                            variant="light"
                            title="Portal invitation pending"
                            role="status"
                          >
                            <Text size="sm">
                              {deliveryPhysicianLabel} has portal contacts, but
                              none have activated their accounts yet. Reports
                              will be available in the portal once a contact
                              completes registration.
                            </Text>
                          </Alert>
                        )}
                        <Text size="sm" c="dimmed">
                          Reports will be routed to the physician&apos;s portal.
                          Manage portal contacts in the Portal Access section on
                          the physician&apos;s record.
                        </Text>
                      </Stack>
                    )}
                  </>
                )}

                {methodName.includes("remote print") && (
                  <TextInput
                    label="Connection Identifier"
                    placeholder="e.g. clinic-printer-001"
                    description="Must match the identifier in the Remote Print client app at the clinic"
                    withAsterisk
                    value={deliveryRemoteConnectionIdentifier}
                    onChange={(e) =>
                      setDeliveryRemoteConnectionIdentifier(
                        e.currentTarget.value
                      )
                    }
                  />
                )}

                {/* Show phone field only for physical delivery methods (mail, ship) */}
                {!methodName.startsWith("portal") &&
                  !methodName.startsWith("hl7") &&
                  !methodName.startsWith("email") &&
                  !methodName.startsWith("fax") &&
                  methodName && (
                    <TextInput
                      label="Phone (optional)"
                      placeholder="Enter phone number"
                      value={deliveryPhone}
                      onChange={(e) => setDeliveryPhone(e.currentTarget.value)}
                    />
                  )}
              </Stack>
            </Modal.Body>

            <Divider />
            <Group justify="space-between" p="md">
              <Button variant="default" onClick={closeDeliveryModal}>
                Cancel
              </Button>
              {!portalNeedsSetup && (
                <Button
                  onClick={() => {
                    if (!deliveryMethodId) {
                      notifications.show({
                        color: "orange",
                        message: "Please select a delivery method",
                      });
                      return;
                    }
                    if (
                      methodName.includes("remote print") &&
                      !deliveryRemoteConnectionIdentifier
                    ) {
                      notifications.show({
                        color: "red",
                        message:
                          "Connection identifier is required for Remote Print delivery",
                      });
                      return;
                    }
                    void handleDeliverySubmit();
                  }}
                  loading={createDelivery.isPending || updateDelivery.isPending}
                >
                  {editingDelivery ? "Update" : "Add"}
                </Button>
              )}
            </Group>
          </Modal.Content>
        </Modal.Root>

        {selectedMethodIsPortal && deliveryPhysicianId && (
          <AddPortalGrantModal
            opened={portalGrantModalOpened}
            onClose={handleClosePortalGrantModal}
            subjectType="physician"
            subjectId={deliveryPhysicianId}
            subjectName={deliveryPhysicianLabel}
            mode="primary"
            primaryDefaults={deliveryPortalPrimaryDefaults}
          />
        )}

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Physicians"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Select
              label="Status"
              placeholder="Select status..."
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
              ]}
              value={pendingFilters.status || "all"}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  status: (value as "all" | "active" | "inactive") || "all",
                })
              }
            />

            <MultiSelect
              label="Preferred Pathologist"
              placeholder="Select pathologists..."
              data={pathologistOptions}
              value={pendingFilters.preferredPathologistIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  preferredPathologistIds:
                    values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <MultiSelect
              label="Report Template"
              placeholder="Select templates..."
              data={templateOptions}
              value={pendingFilters.reportTemplateIds || []}
              onChange={(values) =>
                setPendingFilters({
                  ...pendingFilters,
                  reportTemplateIds: values.length > 0 ? values : undefined,
                })
              }
              searchable
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Remove Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Remove Delivery Method"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to remove the{" "}
              <strong>
                {itemToDelete?.type === "delivery"
                  ? (itemToDelete.item as PhysicianReportDelivery)
                      .reportDeliveryMethod?.name
                  : ""}
              </strong>{" "}
              delivery method from this physician?
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteDelivery.isPending}
              >
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
